import { translate, type Vars } from '$lib/i18n';
import { PAGES } from '$lib/utils/constants';
import enUS from '../locales/en-US.json';

type L10nLookup = (key: string, vars?: Vars) => string;

/**
 * Query string keys used by login URL flows (session banners, return-after-login).
 */
export const LOGIN_URL_QUERY_KEYS = {
	ERROR_TYPE: 'error_type',
	REDIRECT_TO: 'redirect_to',
} as const;

/**
 * Allowed values for the `error_type` query parameter on the login route.
 */
export const LOGIN_ERROR_TYPES = {
	REDIRECT_LOOP: 'redirect_loop',
	TOKEN_EXPIRED: 'token_expired',
	EMAIL_LINK_EXPIRED: 'email_link_expired',
} as const;

export type LoginErrorTypeValue = (typeof LOGIN_ERROR_TYPES)[keyof typeof LOGIN_ERROR_TYPES];

/**
 * HTML/plain copy for `ComponentError` / `ComponentErrorRebrand` when `error_type` is set on the URL.
 * Keys match `LOGIN_ERROR_TYPES` string values. Re-exported on each login variant page config.
 * @param {L10nLookup} l10n - Locale-reactive login lookup.
 * @returns {Record<string, string>} Messages keyed by login error type.
 */
export function getErrorTypeMessages(l10n: L10nLookup): Record<string, string> {
	return {
		[LOGIN_ERROR_TYPES.EMAIL_LINK_EXPIRED]: l10n('login::errorEmailLinkExpired'),
		[LOGIN_ERROR_TYPES.REDIRECT_LOOP]: l10n('login::errorRedirectLoopHtml', {
			supportUrl: PAGES.EXTERNAL.CUSTOMER_SUPPORT_PLUS_CONTACT_US,
		}),
		[LOGIN_ERROR_TYPES.TOKEN_EXPIRED]: l10n('login::errorTokenExpired'),
	};
}

const enUSLookup: L10nLookup = (key, vars) => translate({ en_US: enUS }, ['en_US'], key, vars);

export const errorTypeMessages = getErrorTypeMessages(enUSLookup);

export type LoginErrorBannerPayload = { message: string };

/**
 * Builds the banner payload for `ComponentError` / `ComponentErrorRebrand` from the current query string.
 * @param {URLSearchParams} searchParams - Typically `window.location` or `$page.url` search params.
 * @param {Record<string, string>} messages - Map keyed by `error_type` value; defaults to shared `errorTypeMessages`.
 * @returns {LoginErrorBannerPayload | null} A `{ message }` object when `error_type` matches a known key, otherwise `null`.
 */
export function getMessageForErrorType(
	searchParams: URLSearchParams,
	messages: Record<string, string> = errorTypeMessages
): LoginErrorBannerPayload | null {
	const type = searchParams.get(LOGIN_URL_QUERY_KEYS.ERROR_TYPE);
	if (!type) return null;

	const message = messages[type];
	if (!message) return null;

	return { message };
}

/**
 * Same-origin path prefixes allowed as post-login redirect targets. Includes the
 * classic app (`/abc`) and the separate parent-center app (`/parent-center`).
 */
export const ALLOWED_REDIRECT_PREFIXES = ['/abc', '/parent-center'] as const;

/**
 * Returns a same-origin app path safe to pass to `goto` (or a full navigation) after login. Rejects external URLs and paths outside the allowed prefixes.
 * @param {string | null | undefined} raw - Raw `redirect_to` (or similar) query value, possibly encoded.
 * @returns {string | null} Pathname + search + hash under an allowed prefix, or `null` if unsafe or invalid.
 */
export function getSanitizedPath(raw: string | null | undefined): string | null {
	if (raw == null || typeof raw !== 'string') return null;
	let decoded: string;
	try {
		decoded = decodeURIComponent(raw.trim());
	} catch {
		return null;
	}
	if (!decoded || typeof window === 'undefined') return null;

	let pathname: string;
	let search = '';
	let hash = '';
	try {
		if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
			const u = new URL(decoded);
			if (u.origin !== window.location.origin) return null;
			pathname = u.pathname;
			search = u.search;
			hash = u.hash;
		} else {
			const u = new URL(decoded, window.location.origin);
			pathname = u.pathname;
			search = u.search;
			hash = u.hash;
		}
	} catch {
		return null;
	}

	const isAllowedPrefix = ALLOWED_REDIRECT_PREFIXES.some(
		(prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
	);
	if (!isAllowedPrefix) return null;
	if (pathname.includes('//')) return null;

	return `${pathname}${search}${hash}`;
}

/**
 * When `error_type` is `token_expired` and `redirect_to` sanitizes to an internal path, returns that path for post-login navigation.
 * @param {URLSearchParams} searchParams - Current login URL search params.
 * @returns {string | null} The sanitized internal app path if `error_type` is `token_expired` and `redirect_to` sanitizes to an internal path, otherwise `null`.
 */
export function getURLRedirectPathFromQuery(searchParams: URLSearchParams): string | null {
	return getSanitizedPath(searchParams.get(LOGIN_URL_QUERY_KEYS.REDIRECT_TO));
}

/**
 * Builds the classic login path while forwarding only supported login query params.
 * @param {URLSearchParams} searchParams - Query params from the route handing off to login.
 * @returns {string} The login path with supported query params when present.
 */
export function getLoginPathWithForwardedQuery(searchParams: URLSearchParams): string {
	const loginParams = new URLSearchParams();

	Object.values(LOGIN_URL_QUERY_KEYS).forEach((key) => {
		const value = searchParams.get(key);
		if (value) loginParams.set(key, value);
	});

	const query = loginParams.toString();
	return `${PAGES.INTERNAL.LOGIN}${query ? `?${query}` : ''}`;
}
